import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/axiosConfig';

// Types
export interface Transaction {
  _id: string;
  type: 'earning' | 'withdrawal' | 'bonus' | 'refund' | 'admin_adjustment';
  amount: number;
  description: string;
  taskId?: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  processedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  processedAt?: string;
  notes?: string;
  createdAt: string;
}

export interface WithdrawalRequest {
  _id: string;
  amount: number;
  bankDetails: {
    accountNumber: string;
    bankName: string;
    ifscCode: string;
    accountHolderName: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'processed';
  requestedAt: string;
  processedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  processedAt?: string;
  rejectionReason?: string;
  transactionId?: string;
  notes?: string;
}

export interface BankDetails {
  accountNumber: string;
  bankName: string;
  ifscCode: string;
  branchName: string;
  accountHolderName: string;
  isVerified: boolean;
  verifiedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  verifiedAt?: string;
  documents: Array<{
    documentType: string;
    documentUrl: string;
    uploadedAt: string;
  }>;
}

export interface Wallet {
  _id: string;
  user: string;
  currentBalance: number;
  verifiedBalance: number;
  pendingBalance: number;
  totalEarnings: number;
  totalWithdrawn: number;
  transactions: Transaction[];
  withdrawalRequests: WithdrawalRequest[];
  bankDetails: BankDetails;
  isActive: boolean;
  lastUpdated: string;
  createdAt: string;
  updatedAt: string;
  // Additional calculated fields from backend
  totalPendingWithdrawals?: number;
  totalApprovedWithdrawals?: number;
  availableForWithdrawal?: number;
}

export interface WalletState {
  wallet: Wallet | null;
  transactions: Transaction[];
  withdrawals: WithdrawalRequest[];
  isLoading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

const initialState: WalletState = {
  wallet: null,
  transactions: [],
  withdrawals: [],
  isLoading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
};

// Async thunks
export const getUserWallet = createAsyncThunk(
  'wallet/getUserWallet',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/wallets');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch wallet');
    }
  }
);

export const calculateTaskEarnings = createAsyncThunk(
  'wallet/calculateTaskEarnings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/wallets/calculate-earnings');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to calculate task earnings');
    }
  }
);

export const updateBankDetails = createAsyncThunk(
  'wallet/updateBankDetails',
  async (bankDetails: {
    accountNumber: string;
    bankName: string;
    ifscCode: string;
    branchName: string;
    accountHolderName: string;
  }, { rejectWithValue }) => {
    try {
      const response = await api.put('/wallets/bank-details', { bankDetails });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update bank details');
    }
  }
);

export const requestWithdrawal = createAsyncThunk(
  'wallet/requestWithdrawal',
  async (withdrawalData: {
    amount: number;
    bankDetails: {
      accountNumber: string;
      bankName: string;
      ifscCode: string;
      accountHolderName: string;
    };
  }, { rejectWithValue }) => {
    try {
      const response = await api.post('/wallets/withdraw', withdrawalData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to request withdrawal');
    }
  }
);

export const getUserTransactions = createAsyncThunk(
  'wallet/getUserTransactions',
  async (params: {
    type?: string;
    page?: number;
    limit?: number;
  } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/wallets/transactions', { params });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transactions');
    }
  }
);

export const getUserWithdrawals = createAsyncThunk(
  'wallet/getUserWithdrawals',
  async (params: {
    status?: string;
    page?: number;
    limit?: number;
  } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/wallets/withdrawals', { params });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch withdrawals');
    }
  }
);

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get User Wallet
      .addCase(getUserWallet.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserWallet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.wallet = action.payload;
        state.error = null;
      })
      .addCase(getUserWallet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Calculate Task Earnings
      .addCase(calculateTaskEarnings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(calculateTaskEarnings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.wallet = action.payload.wallet;
        state.error = null;
      })
      .addCase(calculateTaskEarnings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update Bank Details
      .addCase(updateBankDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBankDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.wallet) {
          state.wallet.bankDetails = action.payload.bankDetails;
        }
        state.error = null;
      })
      .addCase(updateBankDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Request Withdrawal
      .addCase(requestWithdrawal.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(requestWithdrawal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Update wallet state with new balance after withdrawal request
        if (state.wallet && action.payload?.data) {
          state.wallet.currentBalance = action.payload.data.currentBalance;
          state.wallet.withdrawalRequests = action.payload.data.withdrawalRequests;
        }
      })
      .addCase(requestWithdrawal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Get User Transactions
      .addCase(getUserTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactions = action.payload.transactions;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getUserTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Get User Withdrawals
      .addCase(getUserWithdrawals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserWithdrawals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.withdrawals = action.payload.withdrawals;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getUserWithdrawals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = walletSlice.actions;
export default walletSlice.reducer;
